import { Router, watchBusy } from "@frui.ts/screens";
import { computed, observable } from "mobx";
import type { interfaces } from "inversify";
import download from "downloadjs";
import EventBus from "services/eventBus";
import OptionsService from "services/optionsService";
import LocalizationService from "services/localizationService";
import AppRouter from "services/router";
import SecurityService from "services/securityService";
import BankExportRepository from "repositories/bankExportRepository";
import BankExportDetailViewModel from "../bankExportDetailViewModel";
import BankExportDetailListViewModel from "../bankExportDetailListViewModel";
import BankTransferListViewModel from "./bankTransferListViewModel";
import NotificationService from "services/notificationService";
import { getBankExportFilename } from "../utils";
import { bound } from "@frui.ts/helpers";
import TransferType from "entities/transferType";
import parseFileNameFromAxiosResponse from "utils/parseFileNameFromAxiosResponse";
import TransferRefundExportDetailResponse from "entities/transferRefundExportDetailResponse";

@Router.registerRoute({ route: ":bankTransferId", name: Router.Self })
export default class BankTransferDetailViewModel extends BankExportDetailViewModel<TransferRefundExportDetailResponse> {
  @observable fileResponse: any = undefined;

  constructor(
    public itemId: string,
    public bankExportDetailListViewModel: ReturnType<typeof BankExportDetailListViewModel.Factory>,
    public repository: BankExportRepository,
    public localizationService: LocalizationService,
    public appRouter: AppRouter,
    public optionsService: OptionsService,
    public eventBus: EventBus,
    public securityService: SecurityService,
    public notificationService: NotificationService
  ) {
    super(
      itemId,
      bankExportDetailListViewModel,
      localizationService,
      appRouter,
      optionsService,
      eventBus,
      securityService,
      notificationService
    );
    this.navigationName = this.itemId;
  }

  @computed
  get editLabel() {
    return this.translate("bankTransferEdit");
  }

  backLink() {
    return this.appRouter.navigate(BankTransferListViewModel);
  }

  protected async loadDetail(): Promise<TransferRefundExportDetailResponse> {
    this.item = await this.repository.getBankTransferById(this.itemId);
    return this.item;
  }

  @bound
  transferPaymentTypeName(paymentType?: TransferType) {
    return paymentType ? this.localizationService.translateEnum("transferType", paymentType) : "";
  }

  @watchBusy
  async download(item: TransferRefundExportDetailResponse) {
    try {
      if (this.allowToDownload) {
        this.fileResponse = await this.repository.getBankTransferReport(item.id);
        download(
          this.fileResponse.data,
          parseFileNameFromAxiosResponse(this.fileResponse, getBankExportFilename(item)),
          "application/zip"
        );
      }
    } catch (exc) {}
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) => {
      return new BankTransferDetailViewModel(
        itemId,
        container.get(BankExportDetailListViewModel.Factory),
        container.get(BankExportRepository),
        container.get(LocalizationService),
        container.get(AppRouter),
        container.get(OptionsService),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(NotificationService)
      );
    };
  }
}
